// src/context/SettingsContext.js
import React, { createContext, useState, useEffect, useCallback } from 'react';
// CORREÇÃO AQUI: Importar 'api' como default, e os outros como nomeados.
import api, { getAppPreferences, saveAppPreferences, uploadAppLogo } from '../services/api';

const APP_PREFERENCES_KEY_LOCAL = 'app_preferences_local'; // Chave para o localStorage (cache)

// Configurações padrão
const DEFAULT_PREFERENCES = {
    defaultShowValues: true,
    headerColor: '#3498db',
    logoUrl: '/imgs/logoHome.png', // URL de fallback/padrão que pode ser relativa ao frontend
};

// Função auxiliar para construir a URL absoluta do logo
const getAbsoluteLogoUrl = (relativePathOrDataUrl) => {
    if (!relativePathOrDataUrl) return DEFAULT_PREFERENCES.logoUrl; // Retorna o default se não houver path

    // Se já é uma Data URL (base64 para pré-visualização) ou já é uma URL absoluta
    if (relativePathOrDataUrl.startsWith('data:image') || relativePathOrDataUrl.startsWith('http://') || relativePathOrDataUrl.startsWith('https://')) {
        return relativePathOrDataUrl;
    }

    // Prepend a baseURL da API para caminhos relativos
    const baseUrl = api.defaults.baseURL; // Pega a baseURL do axios (ex: https://apiocm.mtrsistemas.com.br)
    // Garante que não haja barras duplas, mas que o caminho relativo comece com barra
    return `${baseUrl}${relativePathOrDataUrl.startsWith('/') ? '' : '/'}${relativePathOrDataUrl}`;
};

// Funções para localStorage (cache)
const loadPreferencesFromLocalStorage = () => {
    try {
        const preferences = localStorage.getItem(APP_PREFERENCES_KEY_LOCAL);
        if (preferences) {
            const parsed = JSON.parse(preferences);
            // Ao carregar do cache local, também garante que o logoUrl seja absoluto
            return {
                ...DEFAULT_PREFERENCES,
                ...parsed,
                logoUrl: getAbsoluteLogoUrl(parsed.logoUrl || DEFAULT_PREFERENCES.logoUrl)
            };
        }
    } catch (e) {
        console.error("Erro ao carregar preferências do localStorage:", e);
    }
    return DEFAULT_PREFERENCES;
};

// savePreferencesToLocalStorage salva o objeto com a URL absoluta do logo
const savePreferencesToLocalStorage = (preferences) => {
    try {
        localStorage.setItem(APP_PREFERENCES_KEY_LOCAL, JSON.stringify(preferences));
        window.dispatchEvent(new Event('appPreferencesUpdated'));
    } catch (e) {
        console.error("Erro ao salvar preferências no localStorage:", e);
    }
};

export const SettingsContext = createContext();

export const SettingsProvider = ({ children }) => {
    const [preferences, setPreferences] = useState(loadPreferencesFromLocalStorage);
    const [isLoadingPreferences, setIsLoadingPreferences] = useState(true);
    const [errorLoadingPreferences, setErrorLoadingPreferences] = useState(null);

    useEffect(() => {
        const fetchPreferences = async () => {
            setIsLoadingPreferences(true);
            setErrorLoadingPreferences(null);
            try {
                const response = await getAppPreferences();
                const fetchedPreferences = response.data;
                
                // Converte a URL do logo vinda do backend (que é relativa) para absoluta
                const absoluteLogoUrl = getAbsoluteLogoUrl(fetchedPreferences.logoUrl);

                const newPreferences = {
                    ...DEFAULT_PREFERENCES,
                    ...fetchedPreferences,
                    logoUrl: absoluteLogoUrl // Armazena a URL ABSOLUTA no estado do contexto
                };

                setPreferences(newPreferences);
                savePreferencesToLocalStorage(newPreferences); // Salva a versão absoluta no cache local
            } catch (err) {
                console.warn("Falha ao carregar preferências do backend, usando cache local ou padrões.", err);
                setErrorLoadingPreferences("Erro ao carregar preferências. Usando cache local ou padrões.");
                setPreferences(loadPreferencesFromLocalStorage()); // Fallback para local (já retorna URL absoluta)
            } finally {
                setIsLoadingPreferences(false);
            }
        };

        fetchPreferences(); // Chama na montagem do provedor

        const handlePreferencesStorageChange = () => {
            setPreferences(loadPreferencesFromLocalStorage()); // Garante que updates de outras abas também usem URLs absolutas
        };
        window.addEventListener('appPreferencesUpdated', handlePreferencesStorageChange);
        window.addEventListener('storage', handlePreferencesStorageChange);

        return () => {
            window.removeEventListener('appPreferencesUpdated', handlePreferencesStorageChange);
            window.removeEventListener('storage', handlePreferencesStorageChange);
        };
    }, []); // Dependência vazia, executa uma vez na montagem.

    // Função para atualizar e salvar as preferências no backend
    const updatePreferences = useCallback(async (newPreferencesData, logoFile = null) => {
        setErrorLoadingPreferences(null);
        try {
            let finalLogoUrlToSaveToDb = newPreferencesData.logoUrl; // Pode ser Data URL (pré-vizualização) ou URL absoluta atual

            // 1. Se um novo arquivo foi selecionado, faça o upload
            if (logoFile) {
                const uploadResponse = await uploadAppLogo(logoFile);
                finalLogoUrlToSaveToDb = uploadResponse.data.logoUrl; // Isso é o CAMINHO RELATIVO /uploads/... vindo do backend
            }
            
            // O objeto de preferências que será enviado e salvo no BANCO DE DADOS
            // O banco de dados salva o caminho RELATIVO.
            const preferencesForDb = {
                ...newPreferencesData,
                logoUrl: finalLogoUrlToSaveToDb // Salva o caminho RELATIVO ou Data URL temporária (backend ignora Data URL)
            };

            // 2. Salva as preferências no backend (que por sua vez salva no DB)
            await saveAppPreferences(preferencesForDb);

            // 3. Atualiza o estado local do contexto e o localStorage com a URL ABSOLUTA do logo
            const updatedPreferencesForContext = {
                ...preferencesForDb, // Usa os outros dados (cor, showValues)
                logoUrl: getAbsoluteLogoUrl(finalLogoUrlToSaveToDb) // Garante que o contexto e o cache local tenham a URL ABSOLUTA
            };
            setPreferences(updatedPreferencesForContext);
            savePreferencesToLocalStorage(updatedPreferencesForContext);

            return true; // Sucesso
        } catch (error) {
            console.error("Erro ao salvar preferências no backend:", error);
            setErrorLoadingPreferences("Erro ao salvar preferências. Por favor, tente novamente.");
            throw error;
        }
    }, []);

    return (
        <SettingsContext.Provider value={{
            preferences,
            updatePreferences,
            isLoadingPreferences,
            errorLoadingPreferences
        }}>
            {children}
        </SettingsContext.Provider>
    );
};